import { arrayIncludesAll, BadImplementationException, Debounce, exists, Logger, removeItemFromArray } from '@nu-art/ts-common';
import { StorageKey, Thunder } from '@nu-art/thunderstorm-frontend';
export class SearchContext extends Logger {
    searchItems;
    activeSearchItems;
    addOns;
    activeAddOns;
    sorters;
    minimumRequiredActiveAddons = 0;
    searchDebouncer = new Debounce(() => this.search(), 200, 500);
    saveFilterDebounce;
    filterDictionary;
    _filterChangeListeners = [];
    searchResults;
    searchTime;
    _searchResultChangeListeners = [];
    _retainFilters = false;
    filterStorage;
    constructor(key) {
        super(`SearchContext-${key}`);
        this.searchItems = [];
        this.activeSearchItems = [];
        this.addOns = [];
        this.activeAddOns = [];
        this.sorters = [];
        this.filterStorage = new StorageKey(`search-context-data__${key}`);
        this.filterDictionary = this.filterStorage.get({});
        //Register listeners with a delay to let Thunder instance be created
        setTimeout(() => {
            // @ts-ignore
            Thunder.getInstance().addUIListener(this);
            this.searchItems.forEach(searchItem => {
                // @ts-ignore
                this[searchItem.module.defaultDispatcher.method] = () => this.searchDebouncer.trigger();
            });
        });
    }
    __onSyncStatusChanged = () => this.searchDebouncer.trigger();
    //######################### Factory Logic #########################
    setSearchItems = (items) => {
        this.searchItems = items;
        this.setActiveSearchItems();
        this.logInfo('Set Search Items', this.searchItems);
        return this;
    };
    setAddOns = (addOns) => {
        this.addOns = addOns;
        this.setActiveAddOns();
        this.setActiveSearchItems();
        return this;
    };
    setSorters = (sorters) => {
        this.sorters = sorters;
        return this;
    };
    setMinimumRequiredActiveAddOns = (num) => {
        this.minimumRequiredActiveAddons = num;
        return this;
    };
    retainFilters = () => {
        this._retainFilters = true;
        this.saveFilterDebounce = new Debounce(() => this.saveFilters(), 200, 500);
        return this;
    };
    //######################### Internal Logic #########################
    setActiveAddOns = () => {
        this.activeAddOns = this.addOns.filter(addOn => {
            const currentValue = this.filterDictionary[addOn.key];
            return addOn.isActive(currentValue);
        });
    };
    setActiveSearchItems = () => {
        const activeAddOnKeys = this.activeAddOns.map(addOn => addOn.key);
        if (!activeAddOnKeys.length)
            return this.activeSearchItems = [...this.searchItems];
        this.activeSearchItems = this.searchItems.filter(searchItem => arrayIncludesAll(searchItem.compatibleAddOnKeys, activeAddOnKeys));
    };
    getInitialSearchResults = () => {
        const results = [];
        this.activeSearchItems.forEach(searchItem => {
            const items = exists(searchItem.globalFilter) ? searchItem.module.cache.filter(searchItem.globalFilter) : searchItem.module.cache.all();
            const pointers = items.map(i => ({ dbKey: searchItem.module.dbDef.dbKey, id: i._id, filterResults: {} }));
            results.push(...pointers);
        });
        return results;
    };
    search = () => {
        if (this.activeAddOns.length < this.minimumRequiredActiveAddons) {
            if (this.addOns.length) {
                delete this.searchResults;
                delete this.searchTime;
                this._searchResultChangeListeners.forEach(listener => listener.__onSearchResultsChanged());
            }
            return;
        }
        const startTime = Date.now();
        let searchResults = this.getInitialSearchResults();
        //Map active search item to dbKeys for O(1) access
        const searchItemMap = this.activeSearchItems.reduce((map, item) => {
            map[item.module.dbDef.dbKey] = item;
            return map;
        }, {});
        //Cycle filter the results by active add-ons
        this.activeAddOns.forEach(addOn => {
            let filterValue = this.filterDictionary[addOn.key];
            if (exists(addOn.valueTransform))
                filterValue = addOn.valueTransform(filterValue);
            searchResults = searchResults.filter(result => {
                const searchItem = searchItemMap[result.dbKey];
                result.filterResults[addOn.key] = { value: searchItem.addOnMethods[addOn.methodName](result) };
                const filterResult = addOn.resultFilter(filterValue, result);
                result.filterResults[addOn.key].score = filterResult.score;
                return filterResult.pass;
            });
        });
        //Sort results
        this.sorters.forEach(sorter => {
            const addOn = this.activeAddOns.find(addOn => addOn.key === sorter.key);
            if (!addOn) //No connected active addon was found, do not run sorter
                return;
            sorter.sortFunction(searchResults);
        });
        this.searchResults = searchResults;
        this.searchTime = Date.now() - startTime;
        this._searchResultChangeListeners.forEach(listener => listener.__onSearchResultsChanged());
    };
    saveFilters = () => {
        if (!this._retainFilters)
            return;
        this.filterStorage.set({ ...this.filterDictionary });
    };
    //######################### Public Logic #########################
    getActiveSearchItems = () => [...this.activeSearchItems];
    getSearchResults = () => this.searchResults;
    getSearchTime = () => this.searchTime;
    getAddOns = () => [...this.addOns];
    filter = {
        set: (key, value) => {
            this.filterDictionary[key] = value;
            this.saveFilterDebounce?.trigger();
            this.saveFilters();
            //Re-calculate active addons and search items
            this.setActiveAddOns();
            this.setActiveSearchItems();
            //Notify all listeners that filters have changed
            this._filterChangeListeners.forEach(listener => listener.__onSearchFilterChanged());
            //Trigger search
            this.searchDebouncer.trigger();
        },
        get: (key) => {
            return this.filterDictionary[key];
        },
        clear: (key) => {
            delete this.filterDictionary[key];
            this.saveFilterDebounce?.trigger();
            this.saveFilters();
            //Re-calculate active addons and search items
            this.setActiveAddOns();
            this.setActiveSearchItems();
            //Notify all listeners that filters have changed
            this._filterChangeListeners.forEach(listener => listener.__onSearchFilterChanged());
            //Trigger search
            this.searchDebouncer.trigger();
        }
    };
    filterChangeListeners = {
        register: (renderer) => {
            if (this._filterChangeListeners.includes(renderer))
                return;
            const addOn = this.addOns.find(addOn => addOn.key === renderer.addOn.key);
            if (!addOn) {
                this.logError(renderer, this.addOns);
                throw new BadImplementationException('Trying to register a listener that is not connected to an addon');
            }
            this._filterChangeListeners.push(renderer);
        },
        unregister: (renderer) => {
            removeItemFromArray(this._filterChangeListeners, renderer);
        }
    };
    searchResultChangeListeners = {
        register: (renderer) => {
            if (this._searchResultChangeListeners.includes(renderer))
                return;
            this._searchResultChangeListeners.push(renderer);
        },
        unregister: (renderer) => {
            removeItemFromArray(this._searchResultChangeListeners, renderer);
        }
    };
}
//# sourceMappingURL=SearchContext.js.map